// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React from 'react';
import AppsUtil from './AppsUtil';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import ReactDOM from 'react-dom';

class CSSUtil {
	animationTimer: IntervalID;
	scrollToElemAnimationTimer: IntervalID;
	installerBounceTimer: ?TimeoutID;
	installerPopUpTimer: ?TimeoutID;
	coachMarkDisplayTimeout: ?TimeoutID;
	isStarterPlanRedirect: boolean;

	constructor() {
		if (this.instance) {
			return this.instance;
		}
		this.instance = this;
		this.installerPopUpTimer = null;
		this.animationTimer = null;
		this.scrollToElemAnimationTimer = null;
		this.installerBounceTimer = null;
	}

	getCoachContainerElement() {
		let elem = document.querySelector('#coachMarkContainer');
		if (elem) {
			return elem;
		}
		elem = document.createElement('div');
		elem.id = 'coachMarkContainer';
		document.body.appendChild(elem);
		return elem;
	}

	applyKaizenDownloadStarterClass(hasThor: boolean, removeClass: string) {
		const dialogNode = document.querySelector('.PDPDialogContent.spectrum-Dialog');
		if (dialogNode && dialogNode.parentNode) {
			if (removeClass) {
				this.removeClass(dialogNode.parentNode, 'installThor');
				this.removeClass(dialogNode.parentNode, 'downloadKaizen');
			}
			else {
				if (hasThor !== true) {
					this.addClass(dialogNode.parentNode, 'downloadKaizen');
					if (AppsUtil.getPathData().mainAppsTab === 'starter' || AppsUtil.isStarterPlanRedirect) {
						this.addClass(dialogNode.parentNode, 'starter');
					}
				}
				else {
					this.addClass(dialogNode.parentNode, 'installThor');
				}
			}
		}
		this.isStarterPlanRedirect = false;
	}

	hideTransitionLoader() {
		document.getElementsByTagName('body')[0].classList.remove('bodyOverflowHidden');
		const transitionLoader = document.querySelector('.transition-screen-wrapper.apps');
		if (transitionLoader) {
			transitionLoader.style.display = 'none';
		}
	}

	showDownloadCoachMark(downloadCoachMark: React.Element, autoHide: boolean = true) {
		if (document.querySelector('#popUpInstaller') === null || document.querySelector('#popUpInstaller').style.display === 'none') {
			ReactDOM.render(downloadCoachMark, this.getCoachContainerElement(), () => {
				this.showInstallerPopUp(autoHide);
			});
		}
	}

	showInstallerPopUp(autoHide: boolean) {
		if (this.installerPopUpTimer !== null) {
			clearTimeout(this.installerPopUpTimer);
		}
		const popupElement = document.getElementById('popUpInstaller');
		if ((DeviceDetectionUtil.isSafari() || DeviceDetectionUtil.isChrome()) && popupElement) {
			popupElement.style.display = 'block';
			popupElement.style['z-index'] = 100001;
			if (!DeviceDetectionUtil.isSafari()) {
				const className = 'installer-help-popUp-animate-bottom';
				this.addClass(popupElement, className);
			}
			else {
				const className = 'installer-help-popUp-animate';
				this.addClass(popupElement, className);
			}
			if (typeof autoHide === 'undefined' || autoHide === true) {
				this.hideInstallerPopUp(20000);
			}
		}
	}

	hideInstallerPopUp(timeout: number, onHideCallback: ?Function) {
		const popupElement = document.getElementById('popUpInstaller');
		if (this.installerPopUpTimer) {
			clearTimeout(this.installerPopUpTimer);
		}
		this.installerPopUpTimer = setTimeout(() => {
			if (popupElement) {
				popupElement.style.display = 'none';
				this.removeClass(popupElement, 'installer-help-popUp-animate');
				this.removeClass(popupElement, 'installer-help-popUp-animate-bottom');
			}
			this.installerPopUpTimer = null;
			if (onHideCallback) {
				onHideCallback();
			}
		}, timeout);
		this.removeBounceInstallerPopUpAnimation(timeout);
	}

	showDownloadCoachMarkWithBounce(downloadCoachMark: React.Element, delay: number, onHideCallback: ?Function, shouldHide: ?boolean) {
		this.clearCoachMarkDisplayTimeout();
		this.coachMarkDisplayTimeout = setTimeout(() => {
			if (document.querySelector('#popUpInstaller') === null || document.querySelector('#popUpInstaller').style.display === 'none') {
				ReactDOM.render(downloadCoachMark, this.getCoachContainerElement(), () => {
					this.showBounceFadeInAnimation(onHideCallback, shouldHide);
				});
			}
			this.coachMarkDisplayTimeout = null;
		}, delay);
	}

	clearCoachMarkDisplayTimeout() {
		if (this.coachMarkDisplayTimeout) {
			clearTimeout(this.coachMarkDisplayTimeout);
			this.coachMarkDisplayTimeout = null;
		}
	}

	showBounceFadeInAnimation(onHideCallback: Function, shouldHide: boolean) {
		if (this.installerPopUpTimer !== null) {
			clearTimeout(this.installerPopUpTimer);
		}
		if (this.installerBounceTimer !== null) {
			clearTimeout(this.installerBounceTimer);
		}
		const popupElement = document.getElementById('popUpInstaller');
		if (popupElement) {
			popupElement.style.display = 'block';
			popupElement.style['z-index'] = 100001;
		}
		if (DeviceDetectionUtil.isSafari() || DeviceDetectionUtil.isChrome() || DeviceDetectionUtil.isFirefox() || DeviceDetectionUtil.isEdgeOrIE() || DeviceDetectionUtil.isEdgeBeta()) {
			const BOUNCE_ANIMATION_TIME = 10500;
			if (!shouldHide) {
				// In case of browsers other than Safari, Firefox, Edge & IE, just remove the animation after bounce animation is over
				const className = 'installer-help-popUp-animateBounce-fadein-bottom';
				this.addClass(popupElement, className);
				this.removeBounceInstallerPopUpAnimation(BOUNCE_ANIMATION_TIME);
			}
			else {
				// In case of browsers Safari, Firefox, Edge & IE, remove the coachmark completely after bounce animation is over
				const className = 'installer-help-popUp-animateBounce-fadein';
				this.addClass(popupElement, className);
				this.hideInstallerPopUp(BOUNCE_ANIMATION_TIME, onHideCallback);
			}
		}
	}

	showBounceAnimation() {
		if (this.installerPopUpTimer !== null) {
			clearTimeout(this.installerPopUpTimer);
		}
		const popupElement = document.getElementById('popUpInstaller');
		if ((DeviceDetectionUtil.isChrome() || DeviceDetectionUtil.isFirefox() || DeviceDetectionUtil.isEdgeOrIE()) && popupElement && !this.installerBounceTimer) {
			popupElement.style.display = 'block';
			popupElement.style['z-index'] = 100001;
			const BOUNCE_ANIMATION_TIME = 10500;
			const className = 'installer-help-popUp-animateBounce-bottom';
			this.addClass(popupElement, className);
			this.removeBounceInstallerPopUpAnimation(BOUNCE_ANIMATION_TIME);
		}
	}

	removeBounceInstallerPopUpAnimation(delay: number) {
		if (this.installerBounceTimer) {
			clearTimeout(this.installerBounceTimer);
		}
		this.installerBounceTimer = setTimeout(() => {
			const popupElement = document.getElementById('popUpInstaller');
			if (popupElement) {
				this.removeClass(popupElement, 'installer-help-popUp-animateBounce-fadein-bottom');
				this.removeClass(popupElement, 'installer-help-popUp-animateBounce-fadein');
				this.removeClass(popupElement, 'installer-help-popUp-animateBounce-bottom');
			}
			this.installerBounceTimer = null;
		}, delay);
	}

	addAnimatedInstallerPopup(downloadCoachMarkElem, delay: number, isRestartAnimation: boolean, onHideCallback: Function, shouldHide: boolean) {
		/**
		 *  In case of browsers other than Safari, Firefox, Edge & IE, the coachmark is still being displayed.
		 *  Therefore in case of re-download just re-start the animation.
		 *  For Safari, Edge & IE, we would have to re-display the popup
		 */
		if (isRestartAnimation && !shouldHide) {
			this.showBounceAnimation();
		}
		else {
			this.showDownloadCoachMarkWithBounce(downloadCoachMarkElem, delay, onHideCallback, shouldHide);
		}
	}

	addClass(elem: ?HTMLElement | Node, classname: string) {
		if (elem) {
			const arr = elem.className.split(' ');
			if (arr.indexOf(classname) === -1) {
				elem.className += ` ${classname}`;
			}
		}
	}

	removeClass(elem: ?HTMLElement | Node, classname: string) {
		const regex = new RegExp(`\\b${classname}\\b`, 'g');
		if (elem && elem.className) {
			elem.className = elem.className.replace(regex, '');
		}
	}


	updateClass(selector: string, newClass: string, removeClass: string) {
		const dialogNode = document.querySelector(selector);
		if (dialogNode && dialogNode.parentNode) {
			if (removeClass) {
				this.removeClass(dialogNode.parentNode, newClass);
			}
			else {
				this.addClass(dialogNode.parentNode, newClass);
			}
		}
	}

	animateScrollToTop(decrementValue: number, elem: ?HTMLElement, timerValue: number) {
		const scrollCb = () => {
			if (elem.scrollTop !== 0 && elem.scrollTop > decrementValue) {
				elem.scrollTop = elem.scrollTop - decrementValue;
			}
			else if (elem.scrollTop === 0) {
				clearInterval(this.animationTimer);
			}
			else {
				elem.scrollTop = 0;
			}
		};
		if (elem) {
			this.animationTimer = setInterval(scrollCb, timerValue);
		}
	}

	animateScrollToElem(decrementValue: number, targetPos: number, parEle: ?HTMLElement, timerValue: number) {
		const scrollCb = () => {
			const curPos = parEle.scrollTop;
			const dist = Math.abs(curPos - targetPos);
			if (dist <= 0) {
				this.scrollToElemAnimationTimer
					&& clearInterval(this.scrollToElemAnimationTimer);
			}
			else if (dist <= decrementValue) {
				parEle.scrollTop = targetPos;
				this.scrollToElemAnimationTimer
					&& clearInterval(this.scrollToElemAnimationTimer);
			}
			else if (curPos < targetPos) {
				parEle.scrollTop = curPos + decrementValue;
			}
			else {
				parEle.scrollTop = curPos - decrementValue;
			}
		};
		if (parEle) {
			this.scrollToElemAnimationTimer = setInterval(scrollCb, timerValue);
		}
	}

	addEventListenerToElemId(elemId: string, eventType: string, callback: Function) {
		const elem = document.getElementById(elemId);
		if (elem) {
			elem.addEventListener(eventType, callback);
		}
	}
	
	addEventListenerToElem(elem: HTMLElement, eventType: string, callback: Function) {
		if (elem) {
			elem.addEventListener(eventType, callback);
		}
	}

	removeEventListenerFromElemId(elemId: string, eventType: string, callback: Function) {
		const elem = document.getElementById(elemId);
		if (elem) {
			elem.removeEventListener(eventType, callback);
		}
	}

	removeEventListenerFromElem(elem: HTMLElement, eventType: string, callback: Function) {
		if (elem) {
			elem.removeEventListener(eventType, callback);
		}
	}

	addAppPickerClassToAppContainer() {
		if (document.getElementsByClassName('nuj-app-picker').length <= 1) {
			this.addClass(document.getElementsByClassName('app-container')[0], 'nuj-app-picker');
			AppsUtil.jarvisInitiated.then(() => this.addClass(document.getElementsByClassName('adbMsgClientWrapper')[0], 'nuj-app-picker'));
		}
	}

	removeAppPickerClassToAppContainer() {
		if (document.getElementsByClassName('nuj-app-picker').length !== 0) {
			this.removeClass(document.getElementsByClassName('app-container')[0],'nuj-app-picker');
			AppsUtil.jarvisInitiated.then(() => this.removeClass(document.getElementsByClassName('adbMsgClientWrapper')[0], 'nuj-app-picker'));
		}
	}
}

export default new CSSUtil();
